import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import * as React from 'react';
import { useState } from 'react';
import classNames from 'classnames';
import pickAttrs from "rc-util/es/pickAttrs";
import Overflow from 'rc-overflow';
import TransBtn from '../TransBtn';
import Input from './Input';
import useLayoutEffect from '../hooks/useLayoutEffect';
import { KEY_ALL, getTitleFromNode, getUUID, judgeOverflowing, transformAutoEllipsis } from '../utils/commonUtil';

var onPreventMouseDown = function onPreventMouseDown(event) {
  event.preventDefault();
  event.stopPropagation();
};

var SelectSelector = function SelectSelector(props) {
  var id = props.id,
      prefixCls = props.prefixCls,
      values = props.values,
      open = props.open,
      searchValue = props.searchValue,
      inputRef = props.inputRef,
      placeholder = props.placeholder,
      disabled = props.disabled,
      mode = props.mode,
      showSearch = props.showSearch,
      autoFocus = props.autoFocus,
      autoComplete = props.autoComplete,
      accessibilityIndex = props.accessibilityIndex,
      tabIndex = props.tabIndex,
      removeIcon = props.removeIcon,
      maxTagCount = props.maxTagCount,
      maxTagTextLength = props.maxTagTextLength,
      _props$maxTagPlacehol = props.maxTagPlaceholder,
      maxTagPlaceholder = _props$maxTagPlacehol === void 0 ? function (omittedValues) {
    return "+ ".concat(omittedValues.length, " ...");
  } : _props$maxTagPlacehol,
      tagRender = props.tagRender,
      onToggleOpen = props.onToggleOpen,
      autoEllipsis = props.autoEllipsis,
      restTooltip = props.restTooltip,
      Tooltip = props.tooltip,
      onSelect = props.onSelect,
      onInputChange = props.onInputChange,
      onInputPaste = props.onInputPaste,
      onInputKeyDown = props.onInputKeyDown,
      onInputMouseDown = props.onInputMouseDown,
      onInputCompositionStart = props.onInputCompositionStart,
      onInputCompositionEnd = props.onInputCompositionEnd,
      handleClearAll = props.handleClearAll,
      showDisabledTip = props.showDisabledTip;
  var uuidRef = React.useRef(getUUID());
  var measureRef = React.useRef(null);
  var overflowRef = React.useRef(null);

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      inputWidth = _useState2[0],
      setInputWidth = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      focused = _useState4[0],
      setFocused = _useState4[1];

  var selectionPrefixCls = "".concat(prefixCls, "-selection"); // ===================== Search ======================

  var inputValue = open || mode === 'tags' ? searchValue : '';
  var inputEditable = mode === 'tags' || showSearch && (open || focused);
  React.useEffect(function () {
    if (maxTagCount === 'responsive' && open) {
      document.getElementById(id).focus();
      overflowRef.current.scrollTo({
        top: overflowRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [id, maxTagCount, open, values]); // We measure width and set to the input immediately

  useLayoutEffect(function () {
    setInputWidth(measureRef.current.scrollWidth);
  }, [inputValue]); // ===================== Render ======================
  // >>> Render Selector Node. Includes Item & Rest

  function defaultRenderSelector(title, content, itemDisabled, closable, onClose, key, isRest) {
    var ellipsisConfig = transformAutoEllipsis(autoEllipsis);
    var restConfig = restTooltip === true ? {} : restTooltip;
    var isOverflowing = judgeOverflowing(document.getElementById("overflow-item-".concat(key, "-").concat(uuidRef.current)));
    var showRestTooltip = isRest && restConfig;
    var showSelectorTooltip = !showDisabledTip && (showRestTooltip || ellipsisConfig && isOverflowing) && title && !!Tooltip;
    return /*#__PURE__*/React.createElement("span", {
      className: classNames("".concat(selectionPrefixCls, "-item"), _defineProperty({}, "".concat(selectionPrefixCls, "-item-disabled"), itemDisabled))
    }, /*#__PURE__*/React.createElement(showSelectorTooltip ? Tooltip : React.Fragment, showSelectorTooltip ? _objectSpread({
      title: title,
      overlayClassName: classNames("".concat(prefixCls, "-item-content-tooltip"), _defineProperty({}, "".concat(prefixCls, "-item-content-tooltip-rest"), isRest)),
      destroyTooltipOnHide: true
    }, isRest ? restConfig : ellipsisConfig) : null, /*#__PURE__*/React.createElement("span", {
      id: "overflow-item-".concat(key, "-").concat(uuidRef.current),
      className: classNames("".concat(selectionPrefixCls, "-item-content"), _defineProperty({}, "".concat(prefixCls, "-item-content-overflowing"), isOverflowing))
    }, content)), closable && /*#__PURE__*/React.createElement(TransBtn, {
      className: "".concat(selectionPrefixCls, "-item-remove"),
      onMouseDown: onPreventMouseDown,
      onClick: onClose,
      customizeIcon: removeIcon
    }, "\xD7"));
  }

  function customizeRenderSelector(value, content, itemDisabled, closable, onClose) {
    var onMouseDown = function onMouseDown(e) {
      onPreventMouseDown(e);
      onToggleOpen(!open);
    };

    return /*#__PURE__*/React.createElement("span", {
      onMouseDown: onMouseDown
    }, tagRender({
      label: content,
      value: value,
      disabled: itemDisabled,
      closable: closable,
      onClose: onClose
    }));
  }

  function renderItem(_ref) {
    var itemDisabled = _ref.disabled,
        label = _ref.label,
        value = _ref.value,
        key = _ref.key,
        title = _ref.title;
    var closable = !disabled && !itemDisabled;
    var displayLabel = label;

    if (typeof maxTagTextLength === 'number') {
      if (typeof label === 'string' || typeof label === 'number') {
        var strLabel = String(displayLabel);

        if (strLabel.length > maxTagTextLength) {
          displayLabel = "".concat(strLabel.slice(0, maxTagTextLength), "...");
        }
      }
    }

    var titleNode = getTitleFromNode(displayLabel, title);

    var onClose = function onClose(event) {
      if (event) event.stopPropagation();

      if (key === KEY_ALL && handleClearAll) {
        handleClearAll();
      } else {
        onSelect(value, {
          selected: false
        });
      }
    };

    return typeof tagRender === 'function' ? customizeRenderSelector(value, displayLabel, itemDisabled, closable, onClose) : defaultRenderSelector(titleNode, displayLabel, itemDisabled, closable, onClose, key);
  }

  function renderRest(omittedValues) {
    var content = typeof maxTagPlaceholder === 'function' ? maxTagPlaceholder(omittedValues) : maxTagPlaceholder; // 浮层内容

    var titleArr = omittedValues.map(function (node) {
      return getTitleFromNode(node.label, node.title);
    }).filter(Boolean);
    var titleNode = titleArr.length ? /*#__PURE__*/React.createElement(React.Fragment, null, omittedValues.map(function (node) {
      return /*#__PURE__*/React.createElement("div", {
        key: node.key,
        className: "".concat(prefixCls, "-item-content-tooltip-item"),
        onMouseDown: function onMouseDown(e) {
          e.stopPropagation();
        },
        onClick: function onClick(e) {
          e.stopPropagation();
        }
      }, getTitleFromNode(node.label, node.title));
    })) : undefined; // 是否有+n

    if (omittedValues.length) {
      var _overflowRef$current;

      (_overflowRef$current = overflowRef.current) === null || _overflowRef$current === void 0 ? void 0 : _overflowRef$current.classList.add("".concat(selectionPrefixCls, "-overflow-hasRest").concat(Math.abs(omittedValues.length).toString().length));
    } else {
      var _overflowRef$current2, _overflowRef$current3, _overflowRef$current4;

      (_overflowRef$current2 = overflowRef.current) === null || _overflowRef$current2 === void 0 ? void 0 : _overflowRef$current2.classList.remove("".concat(selectionPrefixCls, "-overflow-hasRest1"));
      (_overflowRef$current3 = overflowRef.current) === null || _overflowRef$current3 === void 0 ? void 0 : _overflowRef$current3.classList.remove("".concat(selectionPrefixCls, "-overflow-hasRest2"));
      (_overflowRef$current4 = overflowRef.current) === null || _overflowRef$current4 === void 0 ? void 0 : _overflowRef$current4.classList.remove("".concat(selectionPrefixCls, "-overflow-hasRest3"));
    }

    return defaultRenderSelector(titleNode, content, false, undefined, undefined, undefined, true);
  } // >>> Input Node


  var inputNode = /*#__PURE__*/React.createElement("div", {
    className: "".concat(selectionPrefixCls, "-search"),
    style: {
      width: inputWidth
    },
    onFocus: function onFocus() {
      setFocused(true);
    },
    onBlur: function onBlur() {
      setFocused(false);
    }
  }, /*#__PURE__*/React.createElement(Input, {
    ref: inputRef,
    open: open,
    prefixCls: prefixCls,
    id: id,
    inputElement: null,
    disabled: disabled,
    autoFocus: autoFocus,
    autoComplete: autoComplete,
    editable: inputEditable,
    accessibilityIndex: accessibilityIndex,
    value: inputValue,
    onKeyDown: onInputKeyDown,
    onMouseDown: onInputMouseDown,
    onChange: onInputChange,
    onPaste: onInputPaste,
    onCompositionStart: onInputCompositionStart,
    onCompositionEnd: onInputCompositionEnd,
    tabIndex: tabIndex,
    attrs: pickAttrs(props, true)
  }), /*#__PURE__*/React.createElement("span", {
    ref: measureRef,
    className: "".concat(selectionPrefixCls, "-search-mirror"),
    "aria-hidden": true
  }, inputValue, "\xA0")); // >>> Selections

  var selectionNode = /*#__PURE__*/React.createElement(Overflow, {
    ref: overflowRef,
    className: classNames('', _defineProperty({}, "".concat(selectionPrefixCls, "-overflow-show"), open && maxTagCount === 'responsive')),
    prefixCls: "".concat(selectionPrefixCls, "-overflow"),
    data: values,
    renderItem: renderItem,
    renderRest: renderRest,
    suffix: inputNode,
    itemKey: "key",
    maxCount: maxTagCount
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, selectionNode, !values.length && !inputValue && /*#__PURE__*/React.createElement("span", {
    className: "".concat(selectionPrefixCls, "-placeholder")
  }, placeholder));
};

export default SelectSelector;